import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiWordLadder } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const EXAMPLES = [
  { start: 'cat', end: 'dog' },
  { start: 'head', end: 'tail' },
  { start: 'cold', end: 'warm' },
  { start: 'love', end: 'hate' },
  { start: 'lead', end: 'gold' },
];

function getDiff(a, b) {
  return b.split('').map((ch, i) => ({ ch, changed: ch !== a[i] }));
}

export default function WordLadder() {
  const [start, setStart]   = useState('');
  const [end, setEnd]       = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  async function handleSolve(e) {
    e.preventDefault();
    if (!start || !end) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const { data } = await apiWordLadder(start, end);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  function loadExample(ex) {
    setStart(ex.start); setEnd(ex.end); setResult(null); setError('');
  }

  return (
    <div className="game-page">
      <PageSEO page="word-ladder" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🪜</span>
          <h1>Word Ladder Solver</h1>
          <p>Transform one word into another by changing one letter at a time — every step must be a real word!</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="row g-3 mb-3">
              <div className="col-6">
                <label className="fw-bold mb-1">Start Word</label>
                <input
                  className="big-input"
                  value={start}
                  onChange={e => setStart(e.target.value.replace(/[^a-zA-Z]/g,'').toLowerCase())}
                  placeholder="e.g. CAT"
                  maxLength={8}
                />
              </div>
              <div className="col-6">
                <label className="fw-bold mb-1">End Word</label>
                <input
                  className="big-input"
                  value={end}
                  onChange={e => setEnd(e.target.value.replace(/[^a-zA-Z]/g,'').toLowerCase())}
                  placeholder="e.g. DOG"
                  maxLength={8}
                />
              </div>
            </div>
            {start && end && start.length !== end.length && (
              <div className="alert alert-warning alert-custom mb-3">
                Both words must be the same length! ({start.length} vs {end.length} letters)
              </div>
            )}
            <div className="mb-3">
              <small className="text-muted fw-bold me-2">Try examples:</small>
              {EXAMPLES.map(ex => (
                <button
                  key={ex.start+ex.end} type="button"
                  className="btn btn-sm me-1 fw-bold"
                  style={{borderRadius:'50px',background:'var(--border)',border:'none',
                          fontFamily:'Nunito,sans-serif',fontSize:'0.8rem',padding:'0.2rem 0.7rem'}}
                  onClick={() => loadExample(ex)}
                >{ex.start} → {ex.end}</button>
              ))}
            </div>
            <button
              type="submit" className="solve-btn"
              disabled={loading || !start || !end || start.length !== end.length}
            >
              {loading
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Climbing…</>
                : '🪜 Find Ladder'
              }
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {result?.path && (
          <div className="solver-card">
            <div className="ladder-summary">
              <span className="ladder-summary-icon">🪜</span>
              <span>Ladder found in <strong>{result.steps}</strong> step{result.steps !== 1 ? 's' : ''}!</span>
            </div>
            <div className="ladder-chain">
              {result.path.map((word, idx) => {
                const prev = result.path[idx - 1];
                const diff = prev ? getDiff(prev, word) : word.split('').map(ch => ({ch, changed:false}));
                let cls = 'mid';
                if (idx === 0) cls = 'start';
                if (idx === result.path.length - 1) cls = 'end';
                return (
                  <div key={idx} className="ladder-rung">
                    {idx > 0 && <div className="ladder-arrow">▾</div>}
                    <div className={`ladder-row ${cls}`}>
                      <span className="ladder-index">{idx + 1}</span>
                      <div className="ladder-tiles">
                        {diff.map((d, i) => (
                          <span key={i} className={`ladder-tile${d.changed ? ' changed' : ''}`}>
                            {d.ch.toUpperCase()}
                          </span>
                        ))}
                      </div>
                      {idx === 0 && <span className="ladder-tag start-tag">START</span>}
                      {idx === result.path.length - 1 && <span className="ladder-tag end-tag">END</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {result?.error && (
          <div className="solver-card text-center">
            <div style={{fontSize:'3rem'}}>😢</div>
            <p className="fw-bold text-muted">{result.error}</p>
            <p className="text-muted small">Try different words or shorter words (3-4 letters work best)</p>
          </div>
        )}
      <SeoContent game="word-ladder" />
      </div>
    </div>
  );
}
